"use client";
import { useState } from "react";
import type { FormEvent } from "react";

type NewsletterLocale = "es" | "en";

type NewsletterCopy = {
  invalidEmail: string;
  successFallback: string;
  genericError: string;
  networkError: string;
  badge: string;
  title: string;
  descriptionPrefix: string;
  descriptionHighlight: string;
  descriptionSuffix: string;
  emailPlaceholder: string;
  emailAriaLabel: string;
  submitIdle: string;
  submitLoading: string;
  privacyNote: string;
};

const NEWSLETTER_COPY: Record<NewsletterLocale, NewsletterCopy> = {
  es: {
    invalidEmail: "Por favor, introduce un correo válido.",
    successFallback: "¡Gracias por suscribirte!",
    genericError: "Ocurrió un error. Inténtalo de nuevo.",
    networkError: "Error de conexión. Inténtalo de nuevo.",
    badge: "Newsletter Premium",
    title: "Mantente a la vanguardia legal",
    descriptionPrefix: "Recibe alertas en tiempo real sobre el ",
    descriptionHighlight: "visado de nómada digital y Ley Beckham",
    descriptionSuffix: ", cambios en extranjería y consejos exclusivos de nuestros expertos.",
    emailPlaceholder: "Tu mejor correo electrónico",
    emailAriaLabel: "Correo electrónico",
    submitIdle: "Suscribirme",
    submitLoading: "Procesando...",
    privacyNote: "Respetamos tu privacidad. Puedes darte de baja en cualquier momento.",
  },
  en: {
    invalidEmail: "Please enter a valid email.",
    successFallback: "Thanks for subscribing!",
    genericError: "Something went wrong. Please try again.",
    networkError: "Connection error. Please try again.",
    badge: "Premium Newsletter",
    title: "Stay ahead on immigration law",
    descriptionPrefix: "Get real-time alerts about ",
    descriptionHighlight: "the digital nomad visa and the Beckham Law",
    descriptionSuffix: ", immigration updates, and exclusive guidance from our legal team.",
    emailPlaceholder: "Your best email",
    emailAriaLabel: "Email address",
    submitIdle: "Subscribe",
    submitLoading: "Processing...",
    privacyNote: "We respect your privacy. You can unsubscribe at any time.",
  },
};

export default function Newsletter({ locale = "es" }: { locale?: NewsletterLocale }) {
  const [email, setEmail] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState<string>("");
  const copy = NEWSLETTER_COPY[locale];

  const submitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage(copy.invalidEmail);
      return;
    }

    setStatus("loading");

    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, locale }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus("success");
        setEmail("");
        setMessage(data.message || copy.successFallback);
      } else {
        setStatus("error");
        setMessage(data.message || copy.genericError);
      }
    } catch {
      setStatus("error");
      setMessage(copy.networkError);
    }
  };

  return (
    <section className="py-12 bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div
          className="relative bg-[#1e293b] rounded-3xl overflow-hidden shadow-2xl border border-gray-800"
          data-aos="fade-up"
        >
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative flex flex-col lg:flex-row items-center justify-between p-8 md:p-12 lg:p-16 gap-8">
            <div className="text-center lg:text-left lg:w-1/2 space-y-4">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs font-bold uppercase tracking-wider">
                {copy.badge}
              </div>
              <h3 className="text-3xl md:text-4xl font-extrabold text-white">{copy.title}</h3>
              <p className="text-gray-400 text-lg leading-relaxed">
                {copy.descriptionPrefix}
                <span className="text-white font-semibold">{copy.descriptionHighlight}</span>
                {copy.descriptionSuffix}
              </p>
            </div>

            <div className="w-full lg:w-5/12">
              <form className="space-y-4" onSubmit={submitForm}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="flex-1 bg-gray-950 border border-gray-700 focus:border-amber-400 rounded-xl px-5 py-4 text-white placeholder-gray-500 transition-all outline-none"
                    placeholder={copy.emailPlaceholder}
                    aria-label={copy.emailAriaLabel}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === "loading"}
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold rounded-xl transition-all shadow-lg shadow-amber-500/20 disabled:opacity-60"
                  >
                    {status === "loading" ? copy.submitLoading : copy.submitIdle}
                  </button>
                </div>

                {status === "success" && (
                  <p className="text-green-400 text-sm font-medium animate-pulse">✅ {message}</p>
                )}
                {status === "error" && <p className="text-red-400 text-sm font-medium">❌ {message}</p>}

                <p className="text-xs text-gray-500 text-center lg:text-left">{copy.privacyNote}</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
